import {
    addLeadingZerosToTime,
    simpleFormatDate,
    translateDateStringToISO,
    translateISOToDateString,
    utcToCurrent,
    simpleFormatTime,
} from 'as-ui-kit/utils/time';
import { joinCarNumber, parseCarNumber } from 'as-ui-kit/utils/carNumbers';
import {
    capitalize,
    isPascalCase,
    isSnakeCase,
    isUUID,
    joinExisting,
    pascalToSnake,
    snakeToCamel,
    snakeToPascal,
    stripNonNumericChars,
    unCapitalize,
} from 'as-ui-kit/utils/strings';
import { carTypeIds, carVerifyStatus } from 'as-ui-kit/const/cars';
import { emptyGuid, ownershipAttributes } from 'as-ui-kit/const';

const notSimpleDicts = ['AtiDocsDocumentTypes', 'CarTypes'];

/**
 * Конвертируем почти любой массив со словарем (/dictionaries), в нормальный словарик
 */
export const simpleArrayToDict = (array, id = 'Id') => {
    if (!array || !Array.isArray(array)) {
        return array;
    }

    return array.reduce(function (result, item) {
        result[item[id]] = lowerCaseObjectKeys(item);
        return result;
    }, {});
};

/**
 * Создаем клон объекта у которого во всех ключах первая буква маленькая
 */
const lowerCaseObjectKeys = obj => {
    const keys = Object.keys(obj);
    let n = keys.length;
    const newObj = {};
    let key;
    let keyNew;
    while (n--) {
        key = keys[n];
        keyNew = key[0].toLowerCase() + key.slice(1);
        newObj[keyNew] = obj[key];
    }
    return newObj;
};

export const transformObjKeysRecursive = (obj, func) => {
    if (!(obj && (Array.isArray(obj) || typeof obj === 'object'))) {
        return obj;
    }

    const newObj = Array.isArray(obj) ? [] : {};

    for (const key in obj) {
        if (!Object.prototype.hasOwnProperty.call(obj, key)) {
            continue;
        }
        if (obj[key] == null) {
            newObj[func(key)] = obj[key];
        } else {
            if (Array.isArray(obj[key])) {
                newObj[func(key)] = obj[key].map(value => transformObjKeysRecursive(value, func));
            } else if (typeof obj[key] === 'object') {
                newObj[func(key)] = transformObjKeysRecursive(obj[key], func);
            } else {
                newObj[func(key)] = obj[key];
            }
        }
    }

    return newObj;
};

/**
 * Конвертирует рекурсивно объект из PascalCase в camelCase
 */
export const pascalToCamelCaseObj = obj => transformObjKeysRecursive(obj, unCapitalize);

/**
 * Конвертирует рекурсивно объект из camelCase в PascalCase
 */

export const capitalizeObjectKeys = obj => transformObjKeysRecursive(obj, capitalize);

export const convertOnlySimpleDict = (dict, dictName) =>
    notSimpleDicts.includes(dictName) ? dict : simpleArrayToDict(dict);

/**
 * Конвертирует рекурсивно объект из snake_case в Pascal/camelCase
 */
export const snakeToPascalObj = (obj, camelCase = false) => {
    const newObj = transformObjKeysRecursive(obj, snakeToPascal);

    return camelCase ? pascalToCamelCaseObj(newObj) : newObj;
};

export const snakeToCamelObj = obj => transformObjKeysRecursive(obj, snakeToCamel);

export const camelToSnakeObj = obj => transformObjKeysRecursive(obj, pascalToSnake);

export const convertFoundedCities = citiesResp =>
    citiesResp.map(i => {
        // формат: типДанных_значение
        // типДанных: 1-страна, 2-регион, 3-город
        const cityData = i.value.split('_');
        const dataField = pointType => (cityData[0] === pointType ? cityData[1] : '');

        const textParts = i.text.split(',').map(part => part.trim());

        // формируем значение для списка фильтрации в формате
        // страна_регион_город, если значения нет, то в соответствующей позиции будет пустота
        return {
            label: i.text,
            value: `${dataField('0')}_${dataField('1')}_${dataField('2')}`,
            cityName: textParts.length > 0 ? textParts[0] : '',
            regionAndCountry: textParts.length > 1 ? textParts.slice(1).join(', ') : '',
        };
    });

export const convertCities = cities => {
    if (!cities) {
        return {};
    }

    return cities.reduce(function (result, item) {
        result[item.city_id] = {
            id: item.city_id,
            cid: item.country_id,
            city: item.name,
            name: item.clarified_name,
            regionId: item.regionId,
            countryId: item.country_id,
            isRegCenter: item.is_regional_center,
        };
        return result;
    }, {});
};

export const convertGeo = geo => {
    const snaked = snakeToCamelObj(geo);
    const countries = snaked.countries.reduce(function (result, item) {
        result[item.id] = item;
        return result;
    }, {});
    const cities = snaked.cities.reduce(function (result, item) {
        result[item.cityId] = {
            id: item.cityId,
            cid: item.countryId,
            city: item.name,
            cityEng: item.nameEng,
            name: item.name,
            region: item.regionName,
            regionEng: item.regionNameEng,
            rid: item.regionId,
            country: item.countryName,
            countryEng: item.countryNameEng,
            code: item.countryCodeName,
            isRegCenter: item.isRegionalCenter,
            timeZone: item.timeZone,
        };
        return result;
    }, {});
    const regions = snaked.regions.reduce(function (result, item) {
        result[item.id] = item;
        return result;
    }, {});
    return {
        countries: countries,
        regions: regions,
        cities: cities,
    };
};

export const convertGeoCities = (cities) => {
    const snaked = snakeToCamelObj(cities);
    const result = {};
    snaked.geoCities.map(item => {
        const city = item.attributesDictionary;
        const cityId = item.dictionaryItemId;
        result[cityId] = {
            id: cityId,
            cid: city.countryId,
            city: city.cityName,
            region: city.regionName,
            rid: city.regionId,
            country: city.countryName,
            code: city.countryCode,
            timeZone: city.timeZone,
        };});
    return result;
};

const simpleCamelArrayToDict = array => simpleArrayToDict(array, 'id');

export const adaptDictionaries = dictionaries => {
    const adapted = Object.keys(dictionaries).reduce(
        (prev, cur) => ({
            ...prev,
            ...(dictionaries[cur] ? { [cur]: simpleCamelArrayToDict(dictionaries[cur]) } : {}),
        }),
        {}
    );

    if (adapted.cities) {
        adapted.cities = simpleArrayToDict(dictionaries.cities, 'cityId');
    }

    if (adapted.carTypes) {
        adapted.carTypes = simpleArrayToDict(dictionaries.carTypes, 'stringifiedTypeId');
    }

    return adapted;
};

export const fixNumber = (number, isTrailer) => {
    if (!number.endsWith('RUS')) {
        return number;
    }

    return joinCarNumber(parseCarNumber(number, isTrailer)) + ' RUS';
};

export const requisitesToServer = requisites => {
    requisites.ati_id = requisites.firmId;

    if (requisites.legal_address) {
        requisites.legal_address.ati_id = requisites.firmId;
    }
    if (requisites.post_address) {
        requisites.post_address.ati_id = requisites.firmId;
    }

    return requisites;
};

export const docTemplatesToDict = docTemplates => {
    const sortedTemplates = [];
    const filteredDocumentTypes = docTemplates
        .map(t => t.doctype)
        .filter((value, index, self) => self.indexOf(value) === index);

    for (const docType of filteredDocumentTypes) {
        const templatesOfType = docTemplates.filter(t => t.doctype === docType);
        sortedTemplates.push({
            type: docType,
            templates: templatesOfType,
        });
    }

    return sortedTemplates;
};

export const requisitesFromServer = requisite => {
    if (requisite && requisite.signees && requisite.signees.length > 0) {
        const defaultSignee = requisite.signees.find(s => s.is_default);
        if (defaultSignee) {
            requisite.signee_id = defaultSignee.signee_id;
        }
    }

    return snakeToCamelObj(requisite);
};

export const convertCarToEditOfferInformation = car => ({
    Id: car.id,
    Description: car.description,
    Type: car.type,
    BrandId: car.info ? car.info.brandId : null,
    Model: car.info ? car.info.model : null,
    ManufactureYear: car.info ? car.info.manufactureYear : null,
    PhoneNumber: car.phone?.toString() || car.phoneNumber || null,
    Number: car.info ? car.info.number : null,
    Vin: car.info ? car.info.vin : null,
    Vrc: car.info ? car.info.vrc : null,
    CarTypeId: car.body ? car.body.type : null,
    Capacity: car.body ? car.body.capacity : null,
    Volume: car.body ? car.body.volume : null,
    Length: car.dimensions ? car.dimensions.length : null,
    Width: car.dimensions ? car.dimensions.width : null,
    Height: car.dimensions ? car.dimensions.height : null,
    HiddenInFirmInfo: car.hiddenInFirmInfo,
    ModificationRestricted: car.modificationRestricted,
    RelatedId: car.relatedId,
    LastDealRelatedId: car.lastDealRelatedId,
    Scans: car.scans,
    Photos: car.photos,
    Sensors: car.sensors,
    TruckOwnerName: car.truckOwnerName,
    OwnershipType: car.ownershipType,
    CanRefrigerator: car.canRefrigerator,
    HasGenSet: car.hasGenSet,
    CanCarryHeavyContainer: car.canCarryHeavyContainer,
    CanCarryTallContainer: car.canCarryTallContainer,
    Dimension: car.body ? car.body.dimension : null,
    TruckPassportNumber: car.info ? car.info.truckPassportNumber : null,
    ContractScans: car.contractScans,
    Verified: car.verify?.status === carVerifyStatus.verified ?? car.verified,
    ExternalId: car.externalId,
});

export const convertCarsToEditOfferInformation = cars => cars.map(convertCarToEditOfferInformation);

export const scansFromUpperCamel = scans => scans?.map(scanFromUpperCamel) || [];

export const scanFromUpperCamel = scan => ({
    ownerId: scan.OwnerId,
    id: scan.ScanId !== emptyGuid ? scan.ScanId : undefined,
    name: scan.Name,
    path: scan.Path,
    type: scan.Type,
    fileId: scan.FileId
});

export const convertBoardToCamel = board => ({
    boardExchangeDirection: board.BoardExchangeDirection,
    boardType: board.BoardType,
    color: board.Color,
    createdOn: board.CreatedOn,
    id: board.ID,
    logoUrl: board.LogoUrl,
    name: board.Name,
    ownerFirm: board.OwnerFirm,
    updatedOn: board.UpdatedOn,
    viewOnlyOwn: board.viewOnlyOwn,
});

// TODO привести все файлы к одному формату
export const mapPhotoToSource = (carPhoto, car) => ({
    link: carPhoto.path,
    fileName: joinExisting(' ', carPhoto.name, car.description),
});

export const mapLoadFileToSource = file => ({
    link: file.Link,
    fileName: file.Name,
});

export const convertDriverToEditOfferInformation = driver => ({
    PhoneNumber: driver.phone?.phoneString,
    IsAttractedDriver: driver.isAttractedDriver,
    InCargoOwnerBlackList: driver.inCargoOwnerBlackList,
    Id: driver.id,
    FirstName: driver.firstName,
    ModificationRestricted: driver.modificationRestricted,
    SecondName: driver.secondName,
    Patronymic: driver.patronymic,
    PassportSeries: driver.passportSeries,
    PassportNumber: driver.passportNumber,
    PassportIssuedBy: driver.passportIssuedBy,
    Registration: driver.registration,
    PassportWhenIssued: driver.passportWhenIssued,
    DriverLicenseNumber: driver.driverLicenseNumber,
    TrackerPresence: driver.phone?.presence,
    Binding: driver.phone?.binding,
    Scans: convertScansToEditOfferInformation([...driver.passportScans, ...driver.driverLicenseScans], driver.id),
    RelatedId: driver.relatedId,
    LastDealRelatedId: driver.lastDealRelatedId,
    driverInn: driver.driverInn,
    dateOfBirth: driver.dateOfBirth,
    departmentCodeInPassport: driver.departmentCodeInPassport,
    driverLicenseWhenIssued: driver.driverLicenseWhenIssued,
    externalId: driver.externalId,
});

export const convertDriversToEditOfferInformation = drivers => drivers.map(convertDriverToEditOfferInformation);

const convertScansToEditOfferInformation = (scans, ownerId) =>
    scans.map(scan => ({
        OwnerId: ownerId,
        ScanId: scan.id ? ownerId : undefined,
        Name: scan.name,
        Path: scan.path,
        Type: scan.type,
        FileId: scan.fileId,
    }));

export const convertDealHistory = dealsHistory => {
    return [
        ...dealsHistory
            .sort(({ dateTime: dateTimeA }, { dateTime: dateTimeB }) => dateTimeB - dateTimeA)
            .reduce((prev, cur) => {
                const updatedDateTime = utcToCurrent(cur.dateTime);
                const date = simpleFormatDate(updatedDateTime, true, true);
                const time = simpleFormatTime(updatedDateTime);

                const thisDate = prev.get(date) || new Map();
                const thisTime = thisDate.get(time) || [];
                const curTime = [...thisTime, { ...cur }];

                return prev.set(date, thisDate.set(time, curTime));
            }, new Map())
            .entries(),
    ].map(([date, datePart]) => [date, [...datePart.entries()]]);
};

export const dealPointFromServer = dealPoint => {
    const { date, dateTo, time, timeTo, address, lat, lon, cityId, ...rest } = pascalToCamelCaseObj(
        snakeToPascalObj(dealPoint)
    );

    return {
        ...rest,
        dateRange: {
            date: date && translateDateStringToISO(date),
            dateTo: dateTo && translateDateStringToISO(dateTo),
        },
        cityId: cityId,
        timeRange: { time: time, timeTo: timeTo },
        geo: { address, lat, lon },
    };
};

export const dealPointToServer = dealPoint => {
    return {
        deal_point_id: dealPoint.dealPointId,
        date: translateISOToDateString(dealPoint.dateRange?.date),
        time: addLeadingZerosToTime(dealPoint.timeRange?.time),
        lat: dealPoint.geo.lat,
        lon: dealPoint.geo.lon,
        ...(dealPoint.timeRange?.timeTo && { time_to: addLeadingZerosToTime(dealPoint.timeRange?.timeTo) }),
        ...(dealPoint.dateRange?.dateTo && { date_to: translateISOToDateString(dealPoint.dateRange?.dateTo) }),
        ...(dealPoint.geo.address && { address: dealPoint.geo.address }),
        ...(dealPoint.contacts && { contact_persons: dealPoint.contacts }),
        ...(dealPoint.note && { note: dealPoint.note }),
    };
};

export const cleanDealPoint = dealPoint => ({
    dealPointId: dealPoint.dealPointId || null,
    dateRange: {
        date: dealPoint.dateRange?.date || null,
        dateTo: dealPoint.dateRange?.dateTo || null,
    },
    timeRange: {
        time: dealPoint.timeRange.time || null,
        timeTo: dealPoint.timeRange.timeTo || null,
    },
    geo: {
        lat: dealPoint.geo?.lat || null,
        lon: dealPoint.geo?.lon || null,
        address: dealPoint.geo?.address || null,
    },
    note: dealPoint.note || null,
    contacts: dealPoint.contacts || [{ name: '', phone: '' }],
});

export const carToVerificationServiceParams = car => {
    const rawNumber = car.info?.number || car.Number;
    const isRussianCarNumber = rawNumber?.endsWith('RUS');

    if (!isRussianCarNumber) {
        return {};
    }

    const vin = car.info?.vin || car.Vin;

    const isTrailer = car.type === carTypeIds.trailer || car.type === carTypeIds.semitrailer;

    let licensePlate;

    if (rawNumber) {
        const { lettersBeforeNum, num, lettersAfterNum, region } = parseCarNumber(rawNumber, isTrailer);

        licensePlate = `${lettersBeforeNum}${num}${lettersAfterNum}${region}`;
    }

    return {
        ...(licensePlate && { license_plate: licensePlate }),
        ...(vin && { vin }),
    };
};

export const driverToVerificationServiceParams = driver => {
    const firstName = driver.firstName;
    const secondName = driver.secondName;
    const patronymic = driver.patronymic;
    const phoneNumber = driver.phoneNumber;
    const passportSeries = driver.passportSeries;
    const passportNumber = driver.passportNumber;
    const passportWhenIssued = driver.passportWhenIssued;
    const driverLicenseNumber = driver.driverLicenseNumber;

    return {
        ...(secondName && { person_last_name: secondName }),
        ...(firstName && { person_first_name: firstName }),
        ...(patronymic && { person_mid_name: patronymic }),
        ...(phoneNumber && { phone: stripNonNumericChars(phoneNumber) }),
        ...(passportSeries && { passport_series: passportSeries }),
        ...(passportNumber && { passport_number: passportNumber }),
        ...(passportWhenIssued && { passport_date: simpleFormatDate(new Date(passportWhenIssued)) }),
        ...(driverLicenseNumber && { driving_license_number: driverLicenseNumber }),
    };
};

export const organizationToVerificationServiceParams = organization => {
    const innOgrn = organization.inn || organization.ogrn;
    return {
        ...(innOgrn && { inn_ogrn: innOgrn }),
    };
};

export const statusListToOptions = list =>
    list
        .sort((l1, l2) => l1.ordinal_id - l2.ordinal_id)
        .map(item => ({ value: Number(item.status), label: item.status_name, disabled: item.disabled, hidden: item.hidden }));

export const toCamelCase = obj =>
    transformObjKeysRecursive(obj, key => {
        if (isUUID(key)) {
            return key;
        }

        return isSnakeCase(key) ? snakeToCamel(key) : isPascalCase(key) ? unCapitalize(key) : key;
    });

export const toPascalCase = obj =>
    transformObjKeysRecursive(obj, key => {
        if (isUUID(key)) {
            return key;
        }

        return isSnakeCase(key) ? capitalize(snakeToCamel(key)) : capitalize(key);
    });

export const fromCamelCaseToPascalCase = obj =>
    transformObjKeysRecursive(obj, key => capitalize(key));

export const convertOwnerships = ownerships => {
    const snaked = snakeToCamelObj(ownerships);
    const result = {};
    const convertOwnershipAttributesToType = (attributes) => {
        switch (attributes) {
            case ownershipAttributes.LegalPerson:
                return 'LegalPerson';
            case ownershipAttributes.SoleProprietor:
                return 'SoleProprietor';
        }
    };
    snaked.map(item => {
        const ownership = item.attributesDictionary;
        const ownershipId = item.dictionaryItemId;
        result[ownershipId] = {
            id: ownershipId,
            name: ownership.fullOwnShipName,
            short_name: ownership.ownShipName,
            ownership_type: convertOwnershipAttributesToType(ownership.osAttributes)
        };});
    return result;
};
